import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Icon } from '@iconify-icon/react';
import { Button, Checkbox, Modal, PinInput, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useRouter } from 'next/router';

import SocialAuthRow from '@/components/auth/SocialAuthRow';
import AuthLayout from '@/components/layouts/AuthLayout';
import TurnstileVerification, {
  useTurnstileVerification,
} from '@/components/shared/TurnstileVerification';
import {
  clientLoginDestination,
  safeLoginPath,
  storeLoginRedirect,
} from '@/components/sidebar/atlas-migration';
import { setAuthCookies, setKeepMeLoggedIn } from '@/utils/authCookies';
import PageLink from '@/utils/PageLink';
import { trialOnboardingContext } from '@/utils/trialCheckout';
import yup from '@/utils/yup';

const schema = yup
  .object({
    email: yup
      .string()
      .trim()
      .required('Email is required')
      .email('Please enter a valid email address'),
  })
  .required();

type LoginFormData = yup.InferType<typeof schema>;

const authInputClassName =
  'border border-gray-300 bg-white text-sm text-gray-900 caret-primary-600 placeholder:text-gray-500 placeholder:font-normal [color-scheme:light]';
const authOtpInputClassName =
  'border border-gray-300 bg-white text-gray-900 caret-primary-600 [color-scheme:light]';

// The entire login-page SSO surface (the "Log in with SSO" button and the
// admin "Set up SSO" entry point) is dark unless explicitly enabled for the
// web build via NEXT_PUBLIC_SSO_ENABLED. Default off, so prod login is
// visually and behaviorally identical to today until SSO is switched on.
const SSO_UI_ENABLED = process.env.NEXT_PUBLIC_SSO_ENABLED === 'true';

/**
 * Enterprise SSO hand-off. If the email's domain routes to an enabled SSO
 * connection, redirect the browser to the identity provider and return true so
 * the caller stops the normal email-OTP flow. Fail-open: any error (SSO
 * disabled, no connection, network) returns false and normal login proceeds.
 */
async function maybeRedirectToSso(email: string, redirect?: string): Promise<boolean> {
  try {
    const params = new URLSearchParams({ email });
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/auth/sso/discover?${params.toString()}`
    );
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.ssoAvailable && data?.connectionId) {
      const url = new URL(`${process.env.NEXT_PUBLIC_API_URL}/auth/sso/${data.connectionId}/login`);
      if (redirect) url.searchParams.set('returnTo', redirect);
      window.location.href = url.href;
      return true;
    }
  } catch {
    // Fall through to the standard email-OTP login.
  }
  return false;
}

// Separate component that uses Google hooks inside the provider
const LoginFormWithGoogle = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const turnstile = useTurnstileVerification();
  const isTurnstileRequired = turnstile.enabled && !turnstile.token;
  const [restoreModal, setRestoreModal] = useState<{
    open: boolean;
    email: string;
    scheduledDeletionAt: string;
  }>({ open: false, email: '', scheduledDeletionAt: '' });
  const [restoreOtp, setRestoreOtp] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);

  const form = useForm<LoginFormData>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setIsClient(true);
    // Pre-fill email from query parameter if present
    const emailParam = router.query.email as string;
    if (emailParam) {
      form.setValue('email', emailParam);
    }
    // Detect Zendesk Marketplace install flow
    const fromParam = router.query.from as string;
    const botIdParam = router.query.botId as string;
    const reinstallParam = router.query.reinstall as string;
    if (fromParam === 'zendesk' && botIdParam) {
      sessionStorage.setItem('zendesk-marketplace-onboarding', 'true');
      sessionStorage.setItem('zendesk-marketplace-botId', botIdParam);
      if (reinstallParam === 'true') {
        sessionStorage.setItem('zendesk-marketplace-reinstall', 'true');
      } else {
        sessionStorage.removeItem('zendesk-marketplace-reinstall');
      }
    }
  }, [router.query.email, router.query.from, router.query.botId, router.query.reinstall, form]);

  // Check if this is invitation-only login
  const inviteOnly = router.query.inviteOnly === 'true';
  const invitedEmail = router.query.email as string;

  const handleSubmit = form.handleSubmit(async (data) => {
    // Validate email if invitation-only mode
    if (inviteOnly && invitedEmail && data.email !== invitedEmail) {
      showNotification({
        title: 'Wrong Email Address',
        message: `This invitation is for ${invitedEmail} only. Please use the correct email address.`,
        color: 'red',
        autoClose: 8000,
      });
      return;
    }

    if (isTurnstileRequired) {
      showNotification({
        message: 'Please complete the verification check.',
        color: 'red',
      });
      return;
    }

    setIsLoading(true);
    // Enterprise SSO hand-off: if this email's domain routes to an enabled SSO
    // connection, redirect to the IdP instead of the email-OTP flow. No-op when
    // SSO is disabled or the domain has no connection (returns false).
    if (
      SSO_UI_ENABLED &&
      (await maybeRedirectToSso(data.email, safeLoginPath(router.query.redirect)))
    ) {
      return;
    }
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          ...trialOnboardingContext(),
          ...(turnstile.token ? { turnstileToken: turnstile.token } : {}),
        }),
      });

      if (res.ok) {
        // Persist the choice so the email-verification page applies it when
        // it stores the auth cookies after OTP verification.
        setKeepMeLoggedIn(keepSignedIn);
        sessionStorage.setItem('verificationEmail', data.email);
        const { subscription, redirect } = router.query;
        storeLoginRedirect(sessionStorage, redirect);
        router.push({
          pathname: PageLink.emailVerification,
          query: subscription ? { subscription } : undefined,
        });
      } else {
        const error = await res.json();
        // Check if account is scheduled for deletion (30-day grace period)
        // NestJS ForbiddenException hoists `code` and `scheduledDeletionAt` to top level
        const errorCode = error.code || error.message?.code;
        const scheduledDeletionAt = error.scheduledDeletionAt || error.message?.scheduledDeletionAt;
        if (errorCode === 'ACCOUNT_SCHEDULED_DELETION') {
          setRestoreModal({
            open: true,
            email: data.email,
            scheduledDeletionAt,
          });
        } else {
          showNotification({
            title: 'Error',
            message: typeof error.message === 'string' ? error.message : 'Login failed',
            color: 'red',
          });
        }
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Network error occurred',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
      if (turnstile.enabled) turnstile.reset();
    }
  });

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (credentialResponse) => {
      if (isTurnstileRequired) {
        showNotification({
          message: 'Please complete the verification check.',
          color: 'red',
        });
        return;
      }

      setIsGoogleLoading(true);
      try {
        const tokensResponse = await fetch(`${window.location.origin}/api/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: credentialResponse.code }),
        });

        if (!tokensResponse.ok) {
          const errorData = await tokensResponse
            .json()
            .catch(() => ({ message: 'Token exchange failed' }));
          throw new Error(errorData.message || 'Failed to exchange Google authorization code');
        }

        const googleTokens = await tokensResponse.json();

        // Validate that we received an id_token
        if (!googleTokens?.id_token) {
          throw new Error('Invalid response from Google OAuth - missing id_token');
        }

        const loginResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id_token: googleTokens.id_token,
            ...trialOnboardingContext(),
            ...(turnstile.token ? { turnstileToken: turnstile.token } : {}),
          }),
        });

        if (loginResponse.ok) {
          const res = await loginResponse.json();
          setKeepMeLoggedIn(keepSignedIn);
          setAuthCookies(res.data.tokens.accessToken, res.data.tokens.refreshToken);

          router.push(await clientLoginDestination(res.data.tokens.accessToken, router.query));
        } else {
          const errorData = await loginResponse.json();

          throw new Error(errorData.message || 'Google login failed');
        }
      } catch (error) {
        showNotification({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Google login failed',
          color: 'red',
        });
      } finally {
        setIsGoogleLoading(false);
        if (turnstile.enabled) turnstile.reset();
      }
    },
    onError: (error) => {
      showNotification({
        title: 'Error',
        message: error.error_description || 'Login error',
        color: 'red',
      });
    },
  });

  const handleRestoreAccount = async () => {
    if (restoreOtp.length !== 6) return;
    setIsRestoring(true);
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user/restore-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: restoreModal.email, otp: restoreOtp }),
      });
      if (res.ok) {
        showNotification({
          title: 'Account Restored',
          message: 'Your account has been restored. Please log in again.',
          color: 'green',
        });
        setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
        setRestoreOtp('');
      } else {
        const error = await res.json();
        showNotification({
          title: 'Error',
          message:
            typeof error.message === 'string'
              ? error.message
              : 'Failed to restore account. Please try again.',
          color: 'red',
        });
      }
    } catch {
      showNotification({
        title: 'Error',
        message: 'Network error occurred',
        color: 'red',
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Don't render form components during SSR or before client hydration
  if (!isClient) {
    return (
      <div className="flex w-full flex-col gap-9">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/4 rounded bg-gray-200"></div>
          <div className="mb-4 h-10 rounded bg-gray-200"></div>
          <div className="h-10 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <form className="flex w-full flex-col gap-9" onSubmit={handleSubmit}>
          {inviteOnly && invitedEmail && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-2">
                <Icon icon="mdi:information" width={20} className="mt-0.5 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Invitation Login</p>
                  <p className="mt-1 text-sm text-blue-700">
                    You're logging in to accept an invitation for <strong>{invitedEmail}</strong>.
                    Please use this email address only.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Email"
                  placeholder="test@gmail.com"
                  error={fieldState.error?.message}
                  readOnly={inviteOnly}
                  disabled={inviteOnly}
                  classNames={{
                    label: 'mb-1 text-sm font-medium text-gray-800',
                    input: authInputClassName,
                    error: 'text-red-500 text-xs',
                  }}
                />
              )}
            />
            <TurnstileVerification className="mt-4" verification={turnstile} />
            <Checkbox
              className="mt-3"
              size="sm"
              label="Keep me signed in"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.currentTarget.checked)}
              classNames={{ label: 'text-sm text-gray-700' }}
            />
            <Button
              className="mt-4 w-full bg-primary-600"
              type="submit"
              loading={isLoading}
              disabled={isTurnstileRequired}>
              Proceed
            </Button>
          </div>
          <div className="flex w-full items-center">
            <div className="h-px flex-grow bg-gray-300" />
            <span className="mx-4 text-sm text-gray-500">or</span>
            <div className="h-px flex-grow bg-gray-300" />
          </div>

          {/*
            Alternative sign-in methods. When SSO is enabled these render as an
            expanding segmented control (icons at rest, hover/focus to expand);
            with Google only it stays a single full-width button. See
            SocialAuthRow for the responsive/touch fallback behavior.
          */}
          <SocialAuthRow
            methods={[
              {
                key: 'google',
                label: 'Continue with Google',
                icon: <Icon icon="flat-color-icons:google" className="text-xl" />,
                onClick: () => googleLogin(),
                loading: isGoogleLoading,
                disabled: isTurnstileRequired,
              },
              ...(SSO_UI_ENABLED
                ? [
                    {
                      key: 'sso',
                      label: 'Log in with SSO',
                      icon: <Icon icon="mdi:shield-key-outline" className="text-xl" />,
                      onClick: () =>
                        router.push({
                          pathname: '/auth/sso',
                          // Preserve the post-login redirect (e.g. from an auth
                          // guard) so the SSO callback returns the user to where
                          // they intended.
                          query: router.query.redirect
                            ? { redirect: safeLoginPath(router.query.redirect) }
                            : {},
                        }),
                    },
                  ]
                : []),
            ]}
          />

          {SSO_UI_ENABLED && (
            <p className="mb-2 text-center text-xs text-gray-400">
              Admins: sign in, then set up SSO for your team from the profile menu.
            </p>
          )}
        </form>
      </FormProvider>

      {/* Restore Account Modal — OTP verified */}
      <Modal
        opened={restoreModal.open}
        onClose={() => {
          setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
          setRestoreOtp('');
        }}
        title="Account Scheduled for Deletion"
        centered>
        <p className="mb-4 text-sm text-gray-600">
          Your account is scheduled for permanent deletion on{' '}
          <strong>
            {restoreModal.scheduledDeletionAt
              ? new Date(restoreModal.scheduledDeletionAt).toLocaleDateString()
              : ''}
          </strong>
          . A verification code has been sent to your email. Enter it below to restore your account.
        </p>
        <div className="mb-4 flex justify-center">
          <PinInput
            length={6}
            type="number"
            value={restoreOtp}
            onChange={setRestoreOtp}
            size="lg"
            classNames={{ input: authOtpInputClassName }}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="default"
            onClick={() => {
              setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
              setRestoreOtp('');
            }}>
            Cancel
          </Button>
          <Button
            color="green"
            loading={isRestoring}
            disabled={restoreOtp.length !== 6}
            onClick={handleRestoreAccount}>
            Restore My Account
          </Button>
        </div>
      </Modal>
    </>
  );
};

// Basic login form without Google provider/hook dependency
const LoginFormBasic = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const turnstile = useTurnstileVerification();
  const isTurnstileRequired = turnstile.enabled && !turnstile.token;
  const [restoreModal, setRestoreModal] = useState<{
    open: boolean;
    email: string;
    scheduledDeletionAt: string;
  }>({ open: false, email: '', scheduledDeletionAt: '' });
  const [restoreOtp, setRestoreOtp] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);

  const form = useForm<LoginFormData>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setIsClient(true);
    // Pre-fill email from query parameter if present
    const emailParam = router.query.email as string;
    if (emailParam) {
      form.setValue('email', emailParam);
    }
    // Detect Zendesk Marketplace install flow
    const fromParam = router.query.from as string;
    const botIdParam = router.query.botId as string;
    const reinstallParam = router.query.reinstall as string;
    if (fromParam === 'zendesk' && botIdParam) {
      sessionStorage.setItem('zendesk-marketplace-onboarding', 'true');
      sessionStorage.setItem('zendesk-marketplace-botId', botIdParam);
      if (reinstallParam === 'true') {
        sessionStorage.setItem('zendesk-marketplace-reinstall', 'true');
      } else {
        sessionStorage.removeItem('zendesk-marketplace-reinstall');
      }
    }
    if (process.env.NODE_ENV === 'production') {
      // Helpful diagnostic in case env is missing on Vercel
      // eslint-disable-next-line no-console
      console.warn('Google login disabled: NEXT_PUBLIC_GOOGLE_CLIENT_ID is not set.');
    }
  }, [router.query.email, router.query.from, router.query.botId, router.query.reinstall, form]);

  // Check if this is invitation-only login
  const inviteOnly = router.query.inviteOnly === 'true';
  const invitedEmail = router.query.email as string;

  const handleSubmit = form.handleSubmit(async (data) => {
    // Validate email if invitation-only mode
    if (inviteOnly && invitedEmail && data.email !== invitedEmail) {
      showNotification({
        title: 'Wrong Email Address',
        message: `This invitation is for ${invitedEmail} only. Please use the correct email address.`,
        color: 'red',
        autoClose: 8000,
      });
      return;
    }

    if (isTurnstileRequired) {
      showNotification({
        message: 'Please complete the verification check.',
        color: 'red',
      });
      return;
    }

    setIsLoading(true);
    // Enterprise SSO hand-off: if this email's domain routes to an enabled SSO
    // connection, redirect to the IdP instead of the email-OTP flow. No-op when
    // SSO is disabled or the domain has no connection (returns false).
    if (
      SSO_UI_ENABLED &&
      (await maybeRedirectToSso(data.email, safeLoginPath(router.query.redirect)))
    ) {
      return;
    }
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          ...trialOnboardingContext(),
          ...(turnstile.token ? { turnstileToken: turnstile.token } : {}),
        }),
      });

      if (res.ok) {
        // Persist the choice so the email-verification page applies it when
        // it stores the auth cookies after OTP verification.
        setKeepMeLoggedIn(keepSignedIn);
        sessionStorage.setItem('verificationEmail', data.email);
        const { subscription, redirect } = router.query;
        storeLoginRedirect(sessionStorage, redirect);
        router.push({
          pathname: PageLink.emailVerification,
          query: subscription ? { subscription } : undefined,
        });
      } else {
        const error = await res.json();
        // Check if account is scheduled for deletion (30-day grace period)
        // NestJS ForbiddenException hoists `code` and `scheduledDeletionAt` to top level
        const errorCode = error.code || error.message?.code;
        const scheduledDeletionAt = error.scheduledDeletionAt || error.message?.scheduledDeletionAt;
        if (errorCode === 'ACCOUNT_SCHEDULED_DELETION') {
          setRestoreModal({
            open: true,
            email: data.email,
            scheduledDeletionAt,
          });
        } else {
          showNotification({
            title: 'Error',
            message: typeof error.message === 'string' ? error.message : 'Login failed',
            color: 'red',
          });
        }
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Network error occurred',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
      if (turnstile.enabled) turnstile.reset();
    }
  });

  const handleRestoreAccount = async () => {
    if (restoreOtp.length !== 6) return;
    setIsRestoring(true);
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user/restore-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: restoreModal.email, otp: restoreOtp }),
      });
      if (res.ok) {
        showNotification({
          title: 'Account Restored',
          message: 'Your account has been restored. Please log in again.',
          color: 'green',
        });
        setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
        setRestoreOtp('');
      } else {
        const error = await res.json();
        showNotification({
          title: 'Error',
          message:
            typeof error.message === 'string'
              ? error.message
              : 'Failed to restore account. Please try again.',
          color: 'red',
        });
      }
    } catch {
      showNotification({
        title: 'Error',
        message: 'Network error occurred',
        color: 'red',
      });
    } finally {
      setIsRestoring(false);
    }
  };

  if (!isClient) {
    return (
      <div className="flex w-full flex-col gap-9">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/4 rounded bg-gray-200"></div>
          <div className="mb-4 h-10 rounded bg-gray-200"></div>
          <div className="h-10 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <form className="flex w-full flex-col gap-9" onSubmit={handleSubmit}>
          {inviteOnly && invitedEmail && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-2">
                <Icon icon="mdi:information" width={20} className="mt-0.5 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Invitation Login</p>
                  <p className="mt-1 text-sm text-blue-700">
                    You're logging in to accept an invitation for <strong>{invitedEmail}</strong>.
                    Please use this email address only.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Email"
                  placeholder="test@gmail.com"
                  error={fieldState.error?.message}
                  readOnly={inviteOnly}
                  disabled={inviteOnly}
                  classNames={{
                    label: 'mb-1 text-sm font-medium text-gray-800',
                    input: authInputClassName,
                    error: 'text-red-500 text-xs',
                  }}
                />
              )}
            />
            <TurnstileVerification className="mt-4" verification={turnstile} />
            <Checkbox
              className="mt-3"
              size="sm"
              label="Keep me signed in"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.currentTarget.checked)}
              classNames={{ label: 'text-sm text-gray-700' }}
            />
            <Button
              className="mt-4 w-full bg-primary-600"
              type="submit"
              loading={isLoading}
              disabled={isTurnstileRequired}>
              Proceed
            </Button>
          </div>
          <div className="flex w-full items-center">
            <div className="h-px flex-grow bg-gray-300" />
            <span className="mx-4 text-sm text-gray-500">or</span>
            <div className="h-px flex-grow bg-gray-300" />
          </div>
          {SSO_UI_ENABLED && (
            <>
              <Button
                variant="outline"
                className="mb-4 w-full border border-gray-300 text-gray-800"
                leftSection={<Icon icon="mdi:shield-key-outline" className="text-xl" />}
                onClick={() =>
                  router.push({
                    pathname: '/auth/sso',
                    // Preserve the post-login redirect (e.g. from an auth guard)
                    // so the SSO callback returns the user to where they intended.
                    query: router.query.redirect
                      ? { redirect: safeLoginPath(router.query.redirect) }
                      : {},
                  })
                }>
                Log in with SSO
              </Button>
              <p className="mb-2 text-center text-xs text-gray-400">
                Admins: sign in, then set up SSO for your team from the profile menu.
              </p>
            </>
          )}
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
            Google login is temporarily unavailable. Please use email to continue.
          </div>
        </form>
      </FormProvider>

      {/* Restore Account Modal — OTP verified */}
      <Modal
        opened={restoreModal.open}
        onClose={() => {
          setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
          setRestoreOtp('');
        }}
        title="Account Scheduled for Deletion"
        centered>
        <p className="mb-4 text-sm text-gray-600">
          Your account is scheduled for permanent deletion on{' '}
          <strong>
            {restoreModal.scheduledDeletionAt
              ? new Date(restoreModal.scheduledDeletionAt).toLocaleDateString()
              : ''}
          </strong>
          . A verification code has been sent to your email. Enter it below to restore your account.
        </p>
        <div className="mb-4 flex justify-center">
          <PinInput
            length={6}
            type="number"
            value={restoreOtp}
            onChange={setRestoreOtp}
            size="lg"
            classNames={{ input: authOtpInputClassName }}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="default"
            onClick={() => {
              setRestoreModal({ open: false, email: '', scheduledDeletionAt: '' });
              setRestoreOtp('');
            }}>
            Cancel
          </Button>
          <Button
            color="green"
            loading={isRestoring}
            disabled={restoreOtp.length !== 6}
            onClick={handleRestoreAccount}>
            Restore My Account
          </Button>
        </div>
      </Modal>
    </>
  );
};

const LoginPage = () => {
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID as string | undefined;
  if (!clientId) {
    return <LoginFormBasic />;
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <LoginFormWithGoogle />
    </GoogleOAuthProvider>
  );
};

LoginPage.getLayout = (page: React.ReactElement) => (
  <AuthLayout
    pageTitle="Chatbot | Login"
    title="Welcome to Chat.co"
    description="Sign in or start your 14-day trial — turn your knowledge into an AI agent with cited answers in minutes.">
    {page}
  </AuthLayout>
);

// Redirect authenticated users to dashboard
export { getServerSideProps } from '@/utils/authRedirect';

export default LoginPage;
